/** Opening and saving the drafter's OWN file — phase 3 of `docs/DESKTOP-AND-WEB.md`.
 *
 * ⚠⚠ **TWO ROUTES, AND THE SECOND ONE IS NOT A DEGRADED FIRST.** Chrome, Edge and WebView2 have
 * the File System Access API, so `save` writes back to the file the drafter opened — no
 * download, no "(1)" suffix, no second copy in ~/Downloads. Firefox and Safari have `<input
 * type=file>` and a download, which is a DIFFERENT contract: the drafter chooses where it lands
 * and the app cannot overwrite silently. Pretending they are the same is how a drafter ends up
 * with eleven copies of one plan.
 *
 * ⚠ `canWriteBack()` is the question the UI must ask before it offers "Save"; where it is false
 * the command is "Save a copy", because that is what will happen.
 *
 * ⚠⚠ **A HANDLE IS A PERMISSION, AND PERMISSION LAPSES.** A `FileSystemFileHandle` kept across a
 * reload (they are structured-cloneable, so IndexedDB can hold one) comes back needing
 * `requestPermission` — and that call must happen inside a user gesture or it is denied with no
 * prompt. `writeTo` asks, and says so when the answer is no.
 */

/** Tauri's own IPC, where there is one — the desktop app's file dialogs.
 *
 * ⚠⚠ **WebKitGTK HAS NO FILE SYSTEM ACCESS API AND NEVER WILL**, so the desktop app on Linux
 * would fall all the way through to a download it has no manager for. Windows' WebView2 does
 * have it, and using it there and something else here would be two behaviours to keep in step.
 * ⇒ Inside the desktop app the dialogs are the SYSTEM's, through two commands in
 * `desktop/src/main.rs`, on every platform.
 *
 * ⚠ Typed here rather than imported: adding `@tauri-apps/api` to the frontend would put it in
 * the bundle the BROWSER serves too, for a code path no browser can reach.
 */
interface TauriIpc {
  invoke(cmd: string, args?: Record<string, unknown>): Promise<unknown>;
}

function tauri(): TauriIpc | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as Record<string, unknown>;
  return (w.__TAURI_INTERNALS__ as TauriIpc | undefined) ?? null;
}

/** The picker's filter — what pdcad can open. ⚠ `.dwg` first: it is what this office has. */
const TYPES = [{
  description: "Drawings",
  accept: {
    "application/acad": [".dwg"] as string[],
    "image/vnd.dxf": [".dxf"] as string[],
  },
}];

/** The narrow slice of the File System Access API this app uses, declared here because
 *  TypeScript's DOM lib does not ship it in every version. ⚠ Declared, never faked: where the
 *  browser has no implementation `canWriteBack()` is false and none of this is called. */
interface OpenOptions {
  multiple?: boolean;
  types?: typeof TYPES;
  excludeAcceptAllOption?: boolean;
}
interface SaveOptions {
  suggestedName?: string;
  types?: typeof TYPES;
}
type PermissionState_ = "granted" | "denied" | "prompt";
interface FileHandle extends FileSystemFileHandle {
  queryPermission?(d: { mode: "read" | "readwrite" }): Promise<PermissionState_>;
  requestPermission?(d: { mode: "read" | "readwrite" }): Promise<PermissionState_>;
}
interface PickerWindow {
  showOpenFilePicker?(o?: OpenOptions): Promise<FileHandle[]>;
  showSaveFilePicker?(o?: SaveOptions): Promise<FileHandle>;
}

function picker(): PickerWindow {
  return (typeof window === "undefined" ? {} : window) as unknown as PickerWindow;
}

/** Can this browser write back to the file that was opened? ⚠ The desktop app always can — its
 *  dialogs are the system's. */
export function canWriteBack(): boolean {
  return tauri() !== null || typeof picker().showSaveFilePicker === "function";
}

/** A drawing the drafter chose, and the handle to put it back with (where there is one). */
export interface PickedFile {
  name: string;
  bytes: Uint8Array;
  /** `null` on a browser with no File System Access — saving will be a download. */
  handle: FileHandle | null;
}

/** Split what the Rust side sends back: `<name>\0<bytes>`, over the fast IPC path — see
 *  `pick_open`, which carries bytes and nothing else, because a 48 MB drawing as a JSON array of
 *  48 million numbers is what the ordinary command return would be. Empty means "no file": the
 *  drafter cancelled, or the app was not launched with one.
 *
 *  ⚠⚠⚠ **`slice`, NOT `subarray` — AND THE READER TOLD ME WHY.** A subarray SHARES its buffer,
 *  and `openFromDisk` hands the worker `file.bytes.buffer`, which is then the WHOLE frame from
 *  offset 0 — name included. The engine read the first six bytes of the NAME as the file's magic
 *  and said exactly that: `not an R13-R2000 DWG: "hatch_"`. */
async function framed(got: unknown): Promise<PickedFile | null> {
  // ⚠⚠⚠ **THE FILE COMES BACK AS A URL NOW, AND IT USED TO COME BACK AS BYTES.** An
  // `ipc::Response` carrying a drawing stopped working somewhere past 5 MiB and said nothing at
  // all: measured with `qa/bench_desktop_open.py`, a 4.5 MB drawing opened in 2.9 s and a 5.3 MB
  // one left the window reading "No drawing open" with an empty command line — while the Rust
  // half logged `read 5310694 bytes, returning a 5310709-byte frame`. Every drawing in
  // `media/heavy/` is 49-96 MB, so the desktop app could not open one of this office's real
  // files by either route. The command now returns `{name, url}` and this fetches the URL, which
  // goes through the webview's own network stack. See `PICKED` in `desktop/src/main.rs`.
  // ⚠ One use per token — a second fetch of the same URL is a 404, so this must not retry.
  let name = "";
  let url = "";
  try {
    const q = JSON.parse(String(got ?? "")) as { name?: string; url?: string };
    name = q.name || "";
    url = q.url || "";
  } catch { return null; }                        // ⚠ Cancel comes back as an empty pair
  if (!name || !url) return null;
  const r = await fetch(url);
  if (!r.ok) throw new Error(`could not read ${name} — the file went away (${r.status})`);
  return {
    name,
    bytes: new Uint8Array(await r.arrayBuffer()),
    handle: null,                                  // ⚠ saving goes back through `pick_save`
  };
}

/** The drawing the DESKTOP APP WAS LAUNCHED WITH — `pdcad-desktop plan.dwg`, which is what a
 *  file manager runs when a drafter double-clicks a .dwg, and what the `.desktop` entry's
 *  `%f` passes. `null` in a browser, and `null` when the app was started with no file.
 *
 *  ⚠ Not a gesture, so it MUST NOT be a dialog — it is a file the drafter already chose, by
 *  double-clicking it, in an app that had not started yet. */
export async function startupFile(): Promise<PickedFile | null> {
  const t = tauri();
  if (!t) return null;
  return await framed(await t.invoke("startup_file"));
}

/** Open a drawing. ⚠ MUST be called from a user gesture: a picker outside one is rejected, and
 *  the rejection is an `AbortError` that looks exactly like the drafter pressing Cancel. */
export async function openFile(): Promise<PickedFile | null> {
  // ⚠⚠ **THE DESKTOP APP FIRST.** Its webview may also have `showOpenFilePicker` (WebView2
  // does), and using that would put the file behind a handle the app cannot write back through
  // on the platforms whose webview has no such API. One route, every platform.
  const t = tauri();
  if (t) return await framed(await t.invoke("pick_open"));
  const p = picker();
  if (typeof p.showOpenFilePicker === "function") {
    let handles: FileHandle[];
    try {
      handles = await p.showOpenFilePicker({ multiple: false, types: TYPES });
    } catch {
      return null;                                 // ⚠ Cancel is not an error
    }
    const handle = handles[0];
    if (!handle) return null;
    const file = await handle.getFile();
    return { name: file.name, bytes: new Uint8Array(await file.arrayBuffer()), handle };
  }
  return await openWithInput();
}

/** The fallback, as an `<input type=file>` — Firefox and Safari. */
export function openWithInput(): Promise<PickedFile | null> {
  return new Promise((resolve) => {
    if (typeof document === "undefined") return resolve(null);
    const el = document.createElement("input");
    el.type = "file";
    el.accept = ".dwg,.dxf";
    // ⚠ NOT appended to the document: an input in the DOM is a visible control a drafter can
    // click twice. `click()` on a detached input opens the picker just the same.
    el.onchange = async () => {
      const f = el.files?.[0];
      if (!f) return resolve(null);
      resolve({ name: f.name, bytes: new Uint8Array(await f.arrayBuffer()), handle: null });
    };
    // ⚠ `oncancel` is newer than the picker itself; without it a cancelled dialog leaves this
    // promise pending for ever, which is the hang that reads as a broken Open button.
    el.oncancel = () => resolve(null);
    el.click();
  });
}

/** Where a save went, so the UI can say it plainly. */
export type SaveResult =
  | { how: "written"; name: string }
  | { how: "downloaded"; name: string }
  | { how: "denied"; name: string };

/** Write back to the file the drafter opened.
 *
 * ⚠ `requestPermission` needs a user gesture. Called from the Save command that is one, it
 * prompts; called from an autosave timer it is denied with no prompt — which is why autosave is
 * OPFS (`opfs.ts`) and never this.
 */
export async function writeTo(handle: FileHandle, bytes: Uint8Array): Promise<SaveResult> {
  const name = handle.name;
  const q = handle.queryPermission
    ? await handle.queryPermission({ mode: "readwrite" })
    : "granted";
  if (q !== "granted") {
    const r = handle.requestPermission
      ? await handle.requestPermission({ mode: "readwrite" })
      : "denied";
    if (r !== "granted") return { how: "denied", name };
  }
  const w = await handle.createWritable();
  try {
    await w.write(bytes as unknown as BufferSource);
  } finally {
    await w.close();
  }
  return { how: "written", name };
}

/** Save with a picker (a new file, or "Save a copy"), falling back to a download. */
export async function saveAs(bytes: Uint8Array, suggestedName: string): Promise<SaveResult> {
  const t = tauri();
  if (t) {
    // ⚠⚠⚠ **A DWG IS NOT TEXT, AND `TextDecoder` DOES NOT SAY SO — IT SUBSTITUTES.** `pick_save`
    // takes a `String`, so calling it for a .dwg meant decoding the bytes to UTF-8 first, and
    // every byte the decoder did not like became U+FFFD: a 95,011-byte drawing arrived as 101,328
    // bytes and the ODA File Converter answered "Unexpected end of file". The DXF path never
    // showed it because a DXF IS text. So binary goes over as a RAW BODY, and the name with it as
    // a header, and nothing on the way turns it into a string.
    const binary = !suggestedName.toLowerCase().endsWith(".dxf");
    const at = String(binary
      ? await (t as unknown as {
          invoke(cmd: string, body: unknown, opts: unknown): Promise<unknown>;
        }).invoke("pick_save_bytes", bytes,
                  { headers: { "x-suggested-name": suggestedName } })
      : await t.invoke("pick_save", {
          suggested: suggestedName, text: new TextDecoder().decode(bytes),
        }));
    if (!at) return { how: "denied", name: suggestedName };            // ⚠ Cancel
    // ⚠ `!` is how `pick_save` returns the reason — see it. A refusal with no cause is the worst
    // answer a CAD program can give about somebody's file.
    if (at.startsWith("!")) return { how: "denied", name: at.slice(1, 120) };
    return { how: "written", name: at.split("/").pop() || suggestedName };
  }
  const p = picker();
  if (typeof p.showSaveFilePicker === "function") {
    let handle: FileHandle;
    try {
      handle = await p.showSaveFilePicker({ suggestedName, types: TYPES });
    } catch {
      return { how: "denied", name: suggestedName };   // ⚠ Cancel
    }
    return await writeTo(handle, bytes);
  }
  download(bytes, suggestedName);
  return { how: "downloaded", name: suggestedName };
}

/** The last resort — hand the bytes to the browser's download manager. */
export function download(bytes: Uint8Array, name: string): void {
  if (typeof document === "undefined") return;
  const blob = new Blob([bytes as unknown as BlobPart], { type: "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  // ⚠ Revoked on the next turn of the loop, not immediately: Safari has not started the
  // download when `click()` returns and a revoked URL gives it nothing to fetch.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** The name to suggest for a save. ⚠ A drawing opened as `.dwg` and saved by this app is a DXF
 *  today (`dwgio.py`: *"Read DWG; deliver DXF"*), and a file whose contents are not what its
 *  suffix claims is the one thing a CAD app must never hand a client. */
export function suggestName(sourceName: string, ext: string): string {
  const stem = sourceName.replace(/\.[^.]+$/, "") || "drawing";
  return `${stem}.${ext.replace(/^\./, "")}`;
}
